/**
 * DictionariesManagementPage Component
 *
 * Admin page for managing all dictionaries (system + custom).
 * Features:
 * - List all dictionaries with details (name, word count, owner)
 * - Add words to any dictionary with A-Z validation
 * - Remove words from dictionaries
 * - Import dictionary files with preview
 * - Confirmation before modifying ODS dictionary
 *
 * @validates Requirements 13.1, 13.2, 13.3, 13.4, 13.5, 13.6, 13.7, 13.8, 13.9, 13.10
 */

import { useCallback, useState, useRef, useEffect } from 'react';
import {
  type Dictionary,
  type ImportResult,
  DictionaryType,
  VALIDATION,
} from '@spellion/shared';
import { useStore } from '../../store';

// ============================================================================
// Types
// ============================================================================

interface DictionaryWithOwner extends Dictionary {
  ownerEmail?: string;
}

interface AdminDictionaryApiResponse {
  success: boolean;
  data?: DictionaryWithOwner[];
  error?: string | { code: string; message: string };
}

interface AddWordApiResponse {
  success: boolean;
  message?: string;
  error?: string | { code: string; message: string };
}

interface RemoveWordApiResponse {
  success: boolean;
  message?: string;
  error?: string | { code: string; message: string };
}

interface ImportApiResponse {
  success: boolean;
  data?: ImportResult & { dictionary: Dictionary };
  error?: string | { code: string; message: string };
}

interface DeleteApiResponse {
  success: boolean;
  error?: string | { code: string; message: string };
}

interface RenameApiResponse {
  success: boolean;
  data?: { dictionaryId: string; name: string };
  message?: string;
  error?: string | { code: string; message: string };
}

interface WordsApiResponse {
  success: boolean;
  data?: {
    dictionaryId: string;
    dictionaryName: string;
    words: string[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  };
  error?: string | { code: string; message: string };
}

/**
 * Extract error message from API error response
 */
function getErrorMessage(error: string | { code: string; message: string } | undefined, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || fallback;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface UploadState {
  status: UploadStatus;
  progress: number;
  error: string | null;
  result: ImportResult | null;
}

// ============================================================================
// Constants
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const ACCEPTED_EXTENSIONS = ['.json'];

const FILE_TYPE_LABELS: Record<string, string> = {
  json: 'JSON',
};

/** Valid word pattern: A-Z only, 1-15 characters */
const VALID_WORD_REGEX = /^[A-Z]{1,15}$/;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 octets';
  const k = 1024;
  const sizes = ['octets', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.slice(lastDot).toLowerCase() : '';
}

/**
 * Validate word contains only A-Z letters
 * @validates Requirement 13.4
 */
function isValidWord(word: string): boolean {
  return VALID_WORD_REGEX.test(word.toUpperCase());
}

// ============================================================================
// DictionariesManagementPage Component
// ============================================================================

export function DictionariesManagementPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Store state
  const token = useStore((state) => state.token);

  // Local state
  const [dictionaries, setDictionaries] = useState<DictionaryWithOwner[]>([]);
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Filter state
  const [filterType, setFilterType] = useState<'all' | 'system' | 'custom'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Add word state
  const [selectedDictId, setSelectedDictId] = useState<string | null>(null);
  const [wordToAdd, setWordToAdd] = useState('');
  const [addWordError, setAddWordError] = useState<string | null>(null);
  const [addWordSuccess, setAddWordSuccess] = useState<string | null>(null);
  const [isAddingWord, setIsAddingWord] = useState(false);

  // Remove word state
  const [wordToRemove, setWordToRemove] = useState('');
  const [removeWordError, setRemoveWordError] = useState<string | null>(null);
  const [removeWordSuccess, setRemoveWordSuccess] = useState<string | null>(null);
  const [isRemovingWord, setIsRemovingWord] = useState(false);

  // ODS modification confirmation state
  const [showOdsConfirm, setShowOdsConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'add' | 'remove';
    word: string;
    dictionaryId: string;
  } | null>(null);

  // Import state
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    error: null,
    result: null,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dictionaryName, setDictionaryName] = useState('');

  // Delete dictionary state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmDict, setDeleteConfirmDict] = useState<DictionaryWithOwner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Set default dictionary state
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [setDefaultSuccess, setSetDefaultSuccess] = useState<string | null>(null);

  // Toggle active state
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  // Rename dictionary state
  const [renamingDictId, setRenamingDictId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  // Preview modal state
  const [previewDictId, setPreviewDictId] = useState<string | null>(null);
  const [previewDictName, setPreviewDictName] = useState<string>('');
  const [previewWords, setPreviewWords] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewTotalPages, setPreviewTotalPages] = useState(0);
  const [previewSearch, setPreviewSearch] = useState('');

  /**
   * Build request headers with auth token
   */
  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  /**
   * Fetch all dictionaries (admin endpoint)
   * @validates Requirement 13.1
   */
  const fetchDictionaries = useCallback(async () => {
    setIsLoadingDictionaries(true);
    setLoadError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/dictionaries`, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data: AdminDictionaryApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data.error, 'Erreur lors du chargement'));
      }

      if (data.success && data.data) {
        setDictionaries(data.data);
        // Select the default dictionary on first load (when selectedDictId is still null)
        setSelectedDictId(prevSelected => {
          if (prevSelected === null && data.data && data.data.length > 0) {
            // Find the default dictionary first, then fallback to ODS, then first
            const defaultDict = data.data.find((d: DictionaryWithOwner) => d.isDefault);
            const odsDict = data.data.find((d: DictionaryWithOwner) => d.type === DictionaryType.SYSTEM);
            const firstDict = data.data[0];
            return defaultDict?.id || odsDict?.id || firstDict?.id || null;
          }
          return prevSelected;
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      setLoadError(message);
    } finally {
      setIsLoadingDictionaries(false);
    }
  }, [getHeaders]);

  // Fetch dictionaries on mount
  useEffect(() => {
    fetchDictionaries();
  }, [fetchDictionaries]);

  /**
   * Check if selected dictionary is ODS (system)
   * @validates Requirement 13.10
   */
  const isOdsDictionary = useCallback(
    (dictionaryId: string) => {
      const dict = dictionaries.find((d) => d.id === dictionaryId);
      return dict?.type === DictionaryType.SYSTEM;
    },
    [dictionaries]
  );

  /**
   * Handle word addition with ODS confirmation if needed
   * @validates Requirements 13.2, 13.3, 13.4, 13.10
   */
  const handleAddWord = useCallback(async () => {
    if (!selectedDictId || !wordToAdd.trim()) return;

    const normalizedWord = wordToAdd.trim().toUpperCase();

    // Validate word format (A-Z only)
    if (!isValidWord(normalizedWord)) {
      setAddWordError('Seules les lettres A-Z sont autorisées');
      return;
    }

    // Check if ODS - require confirmation
    if (isOdsDictionary(selectedDictId) && !showOdsConfirm) {
      setPendingAction({ type: 'add', word: normalizedWord, dictionaryId: selectedDictId });
      setShowOdsConfirm(true);
      return;
    }

    setIsAddingWord(true);
    setAddWordError(null);
    setAddWordSuccess(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/dictionaries/${selectedDictId}/words`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ word: normalizedWord }),
        }
      );

      const data: AddWordApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data.error, 'Erreur lors de l\'ajout'));
      }

      setAddWordSuccess(`Le mot "${normalizedWord}" a été ajouté au dictionnaire`);
      setWordToAdd('');
      // Refresh dictionaries to update word count
      fetchDictionaries();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      setAddWordError(message);
    } finally {
      setIsAddingWord(false);
      setShowOdsConfirm(false);
      setPendingAction(null);
    }
  }, [selectedDictId, wordToAdd, isOdsDictionary, showOdsConfirm, getHeaders, fetchDictionaries]);

  /**
   * Handle word removal with ODS confirmation if needed
   * @validates Requirements 13.5, 13.6, 13.10
   */
  const handleRemoveWord = useCallback(async () => {
    if (!selectedDictId || !wordToRemove.trim()) return;

    const normalizedWord = wordToRemove.trim().toUpperCase();

    // Check if ODS - require confirmation
    if (isOdsDictionary(selectedDictId) && !showOdsConfirm) {
      setPendingAction({ type: 'remove', word: normalizedWord, dictionaryId: selectedDictId });
      setShowOdsConfirm(true);
      return;
    }

    setIsRemovingWord(true);
    setRemoveWordError(null);
    setRemoveWordSuccess(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/dictionaries/${selectedDictId}/words/${encodeURIComponent(normalizedWord)}`,
        {
          method: 'DELETE',
          headers: getHeaders(),
        }
      );

      const data: RemoveWordApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data.error, 'Erreur lors de la suppression'));
      }

      setRemoveWordSuccess(`Le mot "${normalizedWord}" a été supprimé du dictionnaire`);
      setWordToRemove('');
      // Refresh dictionaries to update word count
      fetchDictionaries();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      setRemoveWordError(message);
    } finally {
      setIsRemovingWord(false);
      setShowOdsConfirm(false);
      setPendingAction(null);
    }
  }, [selectedDictId, wordToRemove, isOdsDictionary, showOdsConfirm, getHeaders, fetchDictionaries]);

  /**
   * Execute pending ODS action after confirmation
   */
  const handleOdsConfirm = useCallback(() => {
    if (!pendingAction) return;
    
    if (pendingAction.type === 'add') {
      handleAddWord();
    } else {
      handleRemoveWord();
    }
  }, [pendingAction, handleAddWord, handleRemoveWord]);

  /**
   * Cancel pending ODS action
   */
  const handleOdsCancel = useCallback(() => {
    setShowOdsConfirm(false);
    setPendingAction(null);
  }, []);

  /**
   * Handle file selection for import
   * @validates Requirement 13.7
   */
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file extension
      const extension = getFileExtension(file.name);
      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        setUploadState({
          status: 'error',
          progress: 0,
          error: 'Format non supporté. Seul le format JSON est accepté.',
          result: null,
        });
        return;
      }

      // Validate file size (10 MB max)
      if (file.size > VALIDATION.MAX_DICTIONARY_FILE_SIZE) {
        setUploadState({
          status: 'error',
          progress: 0,
          error: `Fichier trop volumineux. Taille maximale : ${formatFileSize(VALIDATION.MAX_DICTIONARY_FILE_SIZE)}`,
          result: null,
        });
        return;
      }

      setSelectedFile(file);
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      setDictionaryName(baseName);
      setUploadState({ status: 'idle', progress: 0, error: null, result: null });
    },
    []
  );

  /**
   * Handle dictionary import
   * @validates Requirements 13.7, 13.8
   */
  const handleUpload = useCallback(async () => {
    if (!selectedFile || !dictionaryName.trim()) return;

    setUploadState({
      status: 'uploading',
      progress: 0,
      error: null,
      result: null,
    });

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', dictionaryName.trim());

      // Use XMLHttpRequest for upload progress
      const xhr = new XMLHttpRequest();

      const uploadPromise = new Promise<ImportApiResponse>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadState((prev) => ({ ...prev, progress }));
          }
        });

        xhr.addEventListener('load', () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(getErrorMessage(response.error, 'Erreur lors de l\'upload')));
            }
          } catch {
            reject(new Error('Réponse invalide du serveur'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Erreur réseau lors de l\'upload'));
        });

        xhr.open('POST', `${API_BASE_URL}/admin/dictionaries/import`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      const data = await uploadPromise;

      if (data.success && data.data) {
        setUploadState({
          status: 'success',
          progress: 100,
          error: null,
          result: {
            success: data.data.success,
            wordsImported: data.data.wordsImported,
            wordsSkipped: data.data.wordsSkipped,
            errors: data.data.errors,
          },
        });

        // Reset form and refresh list
        setSelectedFile(null);
        setDictionaryName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchDictionaries();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      setUploadState({
        status: 'error',
        progress: 0,
        error: message,
        result: null,
      });
    }
  }, [selectedFile, dictionaryName, token, fetchDictionaries]);

  /**
   * Handle custom dictionary deletion
   * @validates Requirement 13.9
   */
  const handleDelete = useCallback(
    async (dictionaryId: string) => {
      setIsDeleting(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/admin/dictionaries/${dictionaryId}`,
          {
            method: 'DELETE',
            headers: getHeaders(),
          }
        );

        const data: DeleteApiResponse = await response.json();

        if (!response.ok) {
          throw new Error(getErrorMessage(data.error, 'Erreur lors de la suppression'));
        }

        setDeleteConfirmId(null);
        setDeleteConfirmDict(null);
        fetchDictionaries();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        alert(`Erreur: ${message}`);
      } finally {
        setIsDeleting(false);
      }
    },
    [getHeaders, fetchDictionaries]
  );

  /**
   * Open delete confirmation modal
   */
  const openDeleteConfirm = useCallback((dict: DictionaryWithOwner) => {
    setDeleteConfirmId(dict.id);
    setDeleteConfirmDict(dict);
  }, []);

  /**
   * Close delete confirmation modal
   */
  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmId(null);
    setDeleteConfirmDict(null);
  }, []);

  /**
   * Handle setting a dictionary as default
   */
  const handleSetDefault = useCallback(
    async (dictionaryId: string) => {
      setIsSettingDefault(true);
      setSetDefaultSuccess(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/admin/dictionaries/${dictionaryId}/set-default`,
          {
            method: 'POST',
            headers: getHeaders(),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(getErrorMessage(data.error, 'Erreur lors de la définition du dictionnaire par défaut'));
        }

        setSetDefaultSuccess(data.data?.message || 'Dictionnaire par défaut mis à jour');
        fetchDictionaries();
        
        // Auto-select this dictionary for editing when set as default
        setSelectedDictId(dictionaryId);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSetDefaultSuccess(null), 3000);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        alert(`Erreur: ${message}`);
      } finally {
        setIsSettingDefault(false);
      }
    },
    [getHeaders, fetchDictionaries]
  );

  /**
   * Handle toggling dictionary active status
   */
  const handleToggleActive = useCallback(
    async (dictionaryId: string, currentlyActive: boolean) => {
      setIsTogglingActive(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/admin/dictionaries/${dictionaryId}`,
          {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify({ isActive: !currentlyActive }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(getErrorMessage(data.error, 'Erreur lors de la mise à jour'));
        }

        fetchDictionaries();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        alert(`Erreur: ${message}`);
      } finally {
        setIsTogglingActive(false);
      }
    },
    [getHeaders, fetchDictionaries]
  );

  /**
   * Start renaming a dictionary
   */
  const startRenaming = useCallback((dictionaryId: string, currentName: string) => {
    setRenamingDictId(dictionaryId);
    setRenameValue(currentName);
    setRenameError(null);
  }, []);

  /**
   * Cancel renaming
   */
  const cancelRenaming = useCallback(() => {
    setRenamingDictId(null);
    setRenameValue('');
    setRenameError(null);
  }, []);

  /**
   * Handle dictionary rename
   */
  const handleRename = useCallback(
    async () => {
      if (!renamingDictId || !renameValue.trim()) return;

      setIsRenaming(true);
      setRenameError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/admin/dictionaries/${renamingDictId}`,
          {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify({ name: renameValue.trim() }),
          }
        );

        const data: RenameApiResponse = await response.json();

        if (!response.ok) {
          throw new Error(getErrorMessage(data.error, 'Erreur lors du renommage'));
        }

        setRenamingDictId(null);
        setRenameValue('');
        fetchDictionaries();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        setRenameError(message);
      } finally {
        setIsRenaming(false);
      }
    },
    [renamingDictId, renameValue, getHeaders, fetchDictionaries]
  );

  /**
   * Fetch words for preview modal
   */
  const fetchPreviewWords = useCallback(
    async (dictionaryId: string, dictionaryName: string, page: number = 1, search: string = '') => {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewDictId(dictionaryId);
      setPreviewDictName(dictionaryName);
      setPreviewPage(page);

      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '100',
        });
        if (search) {
          params.append('search', search);
        }

        const response = await fetch(
          `${API_BASE_URL}/admin/dictionaries/${dictionaryId}/words?${params}`,
          {
            method: 'GET',
            headers: getHeaders(),
          }
        );

        const data: WordsApiResponse = await response.json();

        if (!response.ok) {
          throw new Error(getErrorMessage(data.error, 'Erreur lors du chargement'));
        }

        if (data.success && data.data) {
          setPreviewWords(data.data.words);
          setPreviewTotal(data.data.pagination.total);
          setPreviewTotalPages(data.data.pagination.totalPages);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        setPreviewError(message);
      } finally {
        setPreviewLoading(false);
      }
    },
    [getHeaders]
  );

  /**
   * Close preview modal
   */
  const closePreview = useCallback(() => {
    setPreviewDictId(null);
    setPreviewDictName('');
    setPreviewWords([]);
    setPreviewPage(1);
    setPreviewTotal(0);
    setPreviewTotalPages(0);
    setPreviewSearch('');
    setPreviewError(null);
  }, []);

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    setUploadState({ status: 'idle', progress: 0, error: null, result: null });
    setSelectedFile(null);
    setDictionaryName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Filter dictionaries based on type and search
  const filteredDictionaries = dictionaries.filter((dict) => {
    // Filter by type
    if (filterType === 'system' && dict.type !== DictionaryType.SYSTEM) return false;
    if (filterType === 'custom' && dict.type !== DictionaryType.CUSTOM) return false;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        dict.name.toLowerCase().includes(query) ||
        dict.ownerEmail?.toLowerCase().includes(query) ||
        dict.id.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Get selected dictionary info
  const selectedDict = dictionaries.find((d) => d.id === selectedDictId);

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Page Header */}
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">
          Gestion des dictionnaires
        </h1>
        <p className="text-slate-600 mt-1">
          Gérez le dictionnaire système et les dictionnaires personnalisés
        </p>
      </div>

      {/* ODS Modification Confirmation Modal */}
      {showOdsConfirm && pendingAction && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <WarningIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">
                  Modification du dictionnaire système
                </h3>
                <p className="text-sm text-slate-500">
                  Cette action affectera le dictionnaire principal
                </p>
              </div>
            </div>

            <p className="text-slate-700 mb-6">
              Êtes-vous sûr de vouloir{' '}
              <strong>
                {pendingAction.type === 'add' ? 'ajouter' : 'supprimer'}
              </strong>{' '}
              le mot <strong className="font-mono">{pendingAction.word}</strong>{' '}
              {pendingAction.type === 'add' ? 'au' : 'du'} dictionnaire système ?
            </p>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleOdsCancel}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleOdsConfirm}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dictionary Confirmation Modal */}
      {deleteConfirmId && deleteConfirmDict && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">
                  Supprimer le dictionnaire
                </h3>
                <p className="text-sm text-slate-500">
                  Cette action est irréversible
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {deleteConfirmDict.type === DictionaryType.SYSTEM ? '📖' : '📚'}
                </span>
                <div>
                  <p className="font-medium text-slate-900">{deleteConfirmDict.name}</p>
                  <p className="text-sm text-slate-500">
                    {deleteConfirmDict.wordCount.toLocaleString('fr-FR')} mots
                    {deleteConfirmDict.ownerEmail && (
                      <> • {deleteConfirmDict.ownerEmail}</>
                    )}
                  </p>
                </div>
                <span className={`ml-auto px-2 py-1 text-xs rounded-full ${
                  deleteConfirmDict.type === DictionaryType.SYSTEM 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {deleteConfirmDict.type === DictionaryType.SYSTEM ? 'Système' : 'Personnalisé'}
                </span>
              </div>
            </div>

            <p className="text-slate-700 mb-6">
              Êtes-vous sûr de vouloir supprimer ce dictionnaire ? 
              Tous les mots qu'il contient seront définitivement perdus.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmDict.id)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <SpinnerIcon className="w-4 h-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dictionary Words Preview Modal */}
      {previewDictId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h3 className="font-semibold text-lg text-slate-900">
                  📖 {previewDictName}
                </h3>
                <p className="text-sm text-slate-500">
                  {previewTotal.toLocaleString('fr-FR')} mots au total
                </p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <CloseIcon className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-slate-100">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Rechercher un mot..."
                  value={previewSearch}
                  onChange={(e) => setPreviewSearch(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchPreviewWords(previewDictId, previewDictName, 1, previewSearch);
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono uppercase"
                />
                <button
                  type="button"
                  onClick={() => fetchPreviewWords(previewDictId, previewDictName, 1, previewSearch)}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                >
                  Rechercher
                </button>
                {previewSearch && (
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewSearch('');
                      fetchPreviewWords(previewDictId, previewDictName, 1, '');
                    }}
                    className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerIcon className="w-8 h-8 text-primary-500 animate-spin" />
                </div>
              ) : previewError ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ErrorIcon className="w-12 h-12 text-red-400 mb-3" />
                  <p className="text-slate-600">{previewError}</p>
                </div>
              ) : previewWords.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  Aucun mot trouvé
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {previewWords.map((word) => (
                    <div
                      key={word}
                      className="px-3 py-2 bg-slate-50 rounded-lg text-sm font-mono text-slate-700 text-center"
                    >
                      {word}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {previewTotalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-200">
                <span className="text-sm text-slate-500">
                  Page {previewPage} sur {previewTotalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchPreviewWords(previewDictId, previewDictName, previewPage - 1, previewSearch)}
                    disabled={previewPage <= 1 || previewLoading}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ← Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchPreviewWords(previewDictId, previewDictName, previewPage + 1, previewSearch)}
                    disabled={previewPage >= previewTotalPages || previewLoading}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dictionaries List - 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h2 className="font-semibold text-lg text-slate-900">
              Tous les dictionnaires ({dictionaries.length})
            </h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'system' | 'custom')}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Tous les types</option>
                <option value="system">Système</option>
                <option value="custom">Custom</option>
              </select>

              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1 sm:w-40"
              />
            </div>
          </div>

          {/* Dictionaries Table */}
          {isLoadingDictionaries ? (
            <div className="flex items-center justify-center py-12">
              <SpinnerIcon className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ErrorIcon className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-slate-600 mb-4">{loadError}</p>
              <button
                type="button"
                onClick={fetchDictionaries}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Réessayer
              </button>
            </div>
          ) : filteredDictionaries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Aucun dictionnaire trouvé
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Success message for set default */}
              {setDefaultSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  ✓ {setDefaultSuccess}
                </div>
              )}
              
              {/* Desktop Table - Hidden on mobile */}
              <table className="w-full hidden sm:table">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="pb-3 text-sm font-medium text-slate-500">Nom</th>
                    <th className="pb-3 text-sm font-medium text-slate-500">Type</th>
                    <th className="pb-3 text-sm font-medium text-slate-500">Mots</th>
                    <th className="pb-3 text-sm font-medium text-slate-500">Statut</th>
                    <th className="pb-3 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>

                <tbody className="text-sm">
                  {filteredDictionaries.map((dict) => (
                    <tr
                      key={dict.id}
                      className={`border-b border-slate-50 ${
                        selectedDictId === dict.id ? 'bg-primary-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-lg ${dict.isDefault ? 'relative' : ''}`}>
                            {dict.isDefault ? '📗' : dict.type === DictionaryType.SYSTEM ? '📖' : '📚'}
                          </span>
                          <div className="flex flex-col flex-1 min-w-0">
                            {renamingDictId === dict.id ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleRename();
                                      if (e.key === 'Escape') cancelRenaming();
                                    }}
                                    className="px-2 py-1 text-sm border border-primary-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 w-full max-w-[150px]"
                                    autoFocus
                                    disabled={isRenaming}
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRename}
                                    disabled={isRenaming || !renameValue.trim()}
                                    className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                                    title="Valider"
                                  >
                                    {isRenaming ? <SpinnerIcon className="w-4 h-4 animate-spin" /> : <CheckIcon className="w-4 h-4" />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelRenaming}
                                    disabled={isRenaming}
                                    className="p-1 text-slate-600 hover:bg-slate-50 rounded disabled:opacity-50"
                                    title="Annuler"
                                  >
                                    <CloseIcon className="w-4 h-4" />
                                  </button>
                                </div>
                                {renameError && (
                                  <span className="text-xs text-red-600">{renameError}</span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group">
                                <span 
                                  className={`font-medium ${dict.isDefault ? 'text-green-700' : 'text-slate-900'} cursor-pointer hover:underline`}
                                  onDoubleClick={() => startRenaming(dict.id, dict.name)}
                                  title="Double-cliquez pour renommer"
                                >
                                  {dict.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => startRenaming(dict.id, dict.name)}
                                  className="p-1 text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Renommer"
                                >
                                  <EditIcon className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                            {dict.isDefault && (
                              <span className="text-xs text-green-600 font-medium">⭐ Dictionnaire actif</span>
                            )}
                            {dict.type === DictionaryType.CUSTOM && dict.ownerEmail && (
                              <span className="text-xs text-slate-500">👤 {dict.ownerEmail}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            dict.type === DictionaryType.SYSTEM
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {dict.type === DictionaryType.SYSTEM ? 'Système' : 'Custom'}
                        </span>
                      </td>
                      <td className="py-3 text-slate-600">
                        {dict.wordCount.toLocaleString('fr-FR')}
                      </td>
                      <td className="py-3">
                        {/* Toggle active/inactive - styled as a switch */}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(dict.id, dict.isActive)}
                            disabled={isTogglingActive || dict.isDefault}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              dict.isActive ? 'bg-green-500' : 'bg-slate-300'
                            } ${(isTogglingActive || dict.isDefault) ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                            title={dict.isDefault ? 'Le dictionnaire actif ne peut pas être désactivé' : (dict.isActive ? 'Cliquez pour désactiver' : 'Cliquez pour activer')}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                dict.isActive ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className={`text-xs ${dict.isActive ? 'text-green-600' : 'text-slate-500'}`}>
                            {dict.isActive ? 'Activé' : 'Désactivé'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Preview button */}
                          <button
                            type="button"
                            onClick={() => fetchPreviewWords(dict.id, dict.name, 1, '')}
                            className="px-2 py-1 text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                            title="Voir les mots du dictionnaire"
                          >
                            🔍
                          </button>

                          <button
                            type="button"
                            onClick={() => setSelectedDictId(dict.id)}
                            className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                              selectedDictId === dict.id
                                ? 'bg-primary-500 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            Éditer
                          </button>

                          {/* Set as default button - only for system dictionaries, not already default and is active */}
                          {!dict.isDefault && dict.isActive && dict.type === DictionaryType.SYSTEM && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(dict.id)}
                              disabled={isSettingDefault}
                              className="px-2 py-1 text-xs bg-green-500 text-white hover:bg-green-600 rounded-lg transition-colors disabled:opacity-50"
                              title="Utiliser ce dictionnaire pour les recherches"
                            >
                              {isSettingDefault ? '...' : 'Utiliser'}
                            </button>
                          )}

                          {/* Delete button - available for all dictionaries except the active one */}
                          {!dict.isDefault && (
                            <button
                              type="button"
                              onClick={() => openDeleteConfirm(dict)}
                              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer ce dictionnaire"
                            >
                              Supprimer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Cards - Visible only on mobile */}
              <div className="sm:hidden space-y-3">
                {filteredDictionaries.map((dict) => (
                  <div
                    key={dict.id}
                    className={`p-4 rounded-xl border ${
                      selectedDictId === dict.id 
                        ? 'bg-primary-50 border-primary-200' 
                        : 'bg-white border-slate-100'
                    }`}
                  >
                    {/* Header: Name + Type badge */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-lg flex-shrink-0">
                          {dict.isDefault ? '📗' : dict.type === DictionaryType.SYSTEM ? '📖' : '📚'}
                        </span>
                        <div className="min-w-0">
                          <p className={`font-medium truncate ${dict.isDefault ? 'text-green-700' : 'text-slate-900'}`}>
                            {dict.name}
                          </p>
                          {dict.isDefault && (
                            <span className="text-xs text-green-600">⭐ Actif</span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs flex-shrink-0 ${
                          dict.type === DictionaryType.SYSTEM
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {dict.type === DictionaryType.SYSTEM ? 'Système' : 'Custom'}
                      </span>
                    </div>

                    {/* Info row: Words count + Status toggle */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-600">
                        {dict.wordCount.toLocaleString('fr-FR')} mots
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(dict.id, dict.isActive)}
                          disabled={isTogglingActive || dict.isDefault}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            dict.isActive ? 'bg-green-500' : 'bg-slate-300'
                          } ${(isTogglingActive || dict.isDefault) ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              dict.isActive ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-xs ${dict.isActive ? 'text-green-600' : 'text-slate-500'}`}>
                          {dict.isActive ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => fetchPreviewWords(dict.id, dict.name, 1, '')}
                        className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
                        title="Voir les mots"
                      >
                        🔍
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedDictId(dict.id)}
                        className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                          selectedDictId === dict.id
                            ? 'bg-primary-500 text-white'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        Éditer
                      </button>
                      {!dict.isDefault && dict.isActive && dict.type === DictionaryType.SYSTEM && (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(dict.id)}
                          disabled={isSettingDefault}
                          className="px-2 py-1 text-xs bg-green-500 text-white rounded-lg"
                        >
                          Utiliser
                        </button>
                      )}
                      {!dict.isDefault && (
                        <button
                          type="button"
                          onClick={() => openDeleteConfirm(dict)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg ml-auto"
                          title="Supprimer"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Word Management Panel - 1 column */}
        <div className="space-y-6">
          {/* Selected Dictionary Info */}
          {selectedDict && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h3 className="font-semibold text-slate-900 mb-3">
                Dictionnaire sélectionné
              </h3>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <span className="text-2xl">
                  {selectedDict.type === DictionaryType.SYSTEM ? '📖' : '📚'}
                </span>
                <div>
                  <p className="font-medium text-slate-900">{selectedDict.name}</p>
                  <p className="text-sm text-slate-500">
                    {selectedDict.wordCount.toLocaleString('fr-FR')} mots
                  </p>
                </div>
                {selectedDict.type === DictionaryType.SYSTEM && (
                  <span className="ml-auto px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    ODS
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Add Word Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Ajouter un mot</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="word-to-add" className="block text-sm font-medium text-slate-700 mb-1">
                  Mot (A-Z uniquement)
                </label>
                <input
                  type="text"
                  id="word-to-add"
                  value={wordToAdd}
                  onChange={(e) => {
                    setWordToAdd(e.target.value.toUpperCase());
                    setAddWordError(null);
                    setAddWordSuccess(null);
                  }}
                  placeholder="EXEMPLE"
                  maxLength={VALIDATION.MAX_WORD_LENGTH}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono uppercase"
                  disabled={!selectedDictId || isAddingWord}
                />
              </div>

              {addWordError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  <ErrorIcon className="w-4 h-4 flex-shrink-0" />
                  {addWordError}
                </div>
              )}

              {addWordSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                  <CheckIcon className="w-4 h-4 flex-shrink-0" />
                  {addWordSuccess}
                </div>
              )}

              <button
                type="button"
                onClick={handleAddWord}
                disabled={!selectedDictId || !wordToAdd.trim() || isAddingWord}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isAddingWord ? (
                  <>
                    <SpinnerIcon className="w-4 h-4 animate-spin" />
                    Ajout en cours...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4" />
                    Ajouter le mot
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Remove Word Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Supprimer un mot</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="word-to-remove" className="block text-sm font-medium text-slate-700 mb-1">
                  Mot à supprimer
                </label>
                <input
                  type="text"
                  id="word-to-remove"
                  value={wordToRemove}
                  onChange={(e) => {
                    setWordToRemove(e.target.value.toUpperCase());
                    setRemoveWordError(null);
                    setRemoveWordSuccess(null);
                  }}
                  placeholder="EXEMPLE"
                  maxLength={VALIDATION.MAX_WORD_LENGTH}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono uppercase"
                  disabled={!selectedDictId || isRemovingWord}
                />
              </div>

              {removeWordError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  <ErrorIcon className="w-4 h-4 flex-shrink-0" />
                  {removeWordError}
                </div>
              )}

              {removeWordSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                  <CheckIcon className="w-4 h-4 flex-shrink-0" />
                  {removeWordSuccess}
                </div>
              )}

              <button
                type="button"
                onClick={handleRemoveWord}
                disabled={!selectedDictId || !wordToRemove.trim() || isRemovingWord}
                className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isRemovingWord ? (
                  <>
                    <SpinnerIcon className="w-4 h-4 animate-spin" />
                    Suppression en cours...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Supprimer le mot
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h2 className="font-semibold text-lg text-slate-900 mb-4">
          Importer un nouveau dictionnaire
        </h2>

        <div className="space-y-4">
          {/* File Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Fichier dictionnaire
            </label>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(',')}
                onChange={handleFileSelect}
                className="sr-only"
                id="dictionary-file"
                disabled={uploadState.status === 'uploading'}
              />

              <label
                htmlFor="dictionary-file"
                className={`
                  flex flex-col items-center justify-center p-6
                  border-2 border-dashed rounded-xl cursor-pointer
                  transition-colors duration-150
                  ${
                    selectedFile
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-slate-300 hover:border-primary-300 hover:bg-slate-50'
                  }
                  ${uploadState.status === 'uploading' ? 'pointer-events-none opacity-60' : ''}
                `}
              >
                {selectedFile ? (
                  <>
                    <FileIcon className="w-8 h-8 text-primary-500 mb-2" />
                    <p className="font-medium text-slate-900">{selectedFile.name}</p>
                    <p className="text-sm text-slate-500">
                      {formatFileSize(selectedFile.size)} •{' '}
                      {FILE_TYPE_LABELS[getFileExtension(selectedFile.name).slice(1)] || 'Fichier'}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        resetUpload();
                      }}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                    >
                      Changer de fichier
                    </button>
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="font-medium text-slate-700">
                      Cliquez pour sélectionner un fichier
                    </p>
                    <p className="text-sm text-slate-500">
                      JSON uniquement (max {formatFileSize(VALIDATION.MAX_DICTIONARY_FILE_SIZE)})
                    </p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Dictionary Name Input */}
          {selectedFile && (
            <div className="space-y-2">
              <label
                htmlFor="dictionary-name"
                className="block text-sm font-medium text-slate-700"
              >
                Nom du dictionnaire
              </label>
              <input
                type="text"
                id="dictionary-name"
                value={dictionaryName}
                onChange={(e) => setDictionaryName(e.target.value)}
                placeholder="Mon dictionnaire"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={uploadState.status === 'uploading'}
                maxLength={100}
              />
            </div>
          )}

          {/* Upload Progress */}
          {uploadState.status === 'uploading' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Import en cours...</span>
                <span className="font-medium text-primary-600">
                  {uploadState.progress}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${uploadState.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Error */}
          {uploadState.status === 'error' && uploadState.error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              <ErrorIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Erreur d'import</p>
                <p className="text-sm">{uploadState.error}</p>
              </div>
              <button
                type="button"
                onClick={resetUpload}
                className="ml-auto p-1 hover:bg-red-100 rounded transition-colors"
                aria-label="Fermer"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Upload Success with Import Report */}
          {uploadState.status === 'success' && uploadState.result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-start gap-3">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-700">
                    Dictionnaire importé avec succès
                  </p>
                  <div className="mt-2 text-sm text-green-600 space-y-1">
                    <p>
                      <strong>{uploadState.result.wordsImported.toLocaleString('fr-FR')}</strong>{' '}
                      mots importés
                    </p>
                    {uploadState.result.wordsSkipped > 0 && (
                      <div className="text-orange-600">
                        <p>
                          <strong>{uploadState.result.wordsSkipped.toLocaleString('fr-FR')}</strong>{' '}
                          mots ignorés (caractères invalides)
                        </p>
                        {uploadState.result.errors && uploadState.result.errors.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer hover:text-orange-700 font-medium">
                              Voir les mots ignorés ({Math.min(uploadState.result.errors.length, 10)} affichés)
                            </summary>
                            <ul className="mt-2 ml-4 space-y-1 text-xs bg-orange-50 p-3 rounded-lg border border-orange-200">
                              {uploadState.result.errors.map((error, index) => (
                                <li key={index} className="font-mono">
                                  {error}
                                </li>
                              ))}
                              {uploadState.result.wordsSkipped > 10 && (
                                <li className="text-orange-500 italic">
                                  ... et {uploadState.result.wordsSkipped - 10} autres mots ignorés
                                </li>
                              )}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetUpload}
                  className="p-1 hover:bg-green-100 rounded transition-colors"
                  aria-label="Fermer"
                >
                  <CloseIcon className="w-4 h-4 text-green-600" />
                </button>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {selectedFile && uploadState.status !== 'success' && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!dictionaryName.trim() || uploadState.status === 'uploading'}
              className="w-full px-6 py-3 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors duration-150 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploadState.status === 'uploading' ? (
                <>
                  <SpinnerIcon className="w-5 h-5 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <UploadIcon className="w-5 h-5" />
                  Importer le dictionnaire
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DictionariesManagementPage;


// ============================================================================
// Icon Components
// ============================================================================

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  );
}
